import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ArrowLeft, Mic, MicOff, Loader2, Sparkles, Heart, Target, Globe, Banknote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const moduleIcons = { passions: Heart, talents: Target, utility: Globe, monetization: Banknote };

const QuickQuiz = () => {
  const navigate = useNavigate();
  const { user, fetchUser } = useAuth();
  const { language } = useLanguage();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const wantsListeningRef = useRef(false);

  const SpeechRecognition = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const speechSupported = !!SpeechRecognition;

  const fetchQuestions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/quick-quiz/questions`);
      setQuestions(res.data);
    } catch { navigate('/dashboard'); }
    finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  const currentQ = questions[currentIndex];
  const progress = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;
  const answeredCount = Object.values(answers).filter(a => a.trim()).length;
  const Icon = currentQ ? (moduleIcons[currentQ.module] || Heart) : Heart;

  const stopListening = () => {
    wantsListeningRef.current = false;
    if (recognitionRef.current) { recognitionRef.current.abort(); recognitionRef.current = null; }
    setIsListening(false);
  };

  const startOneShot = (qId) => {
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.lang = language === 'fr' ? 'fr-FR' : 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onresult = (event) => {
      const text = event.results[0]?.[0]?.transcript?.trim();
      if (text) {
        setAnswers(prev => {
          const current = prev[qId] || '';
          const sep = current && !current.endsWith(' ') ? ' ' : '';
          return { ...prev, [qId]: current + sep + text };
        });
      }
    };
    recognition.onerror = (event) => {
      if (event.error === 'no-speech' && wantsListeningRef.current) { startOneShot(qId); return; }
      if (event.error !== 'aborted') toast.error(language === 'fr' ? 'Erreur micro' : 'Mic error');
      wantsListeningRef.current = false;
      setIsListening(false);
    };
    recognition.onend = () => {
      recognitionRef.current = null;
      if (wantsListeningRef.current) startOneShot(qId);
      else setIsListening(false);
    };
    recognitionRef.current = recognition;
    recognition.start();
  };

  const toggleListening = () => {
    if (isListening) { stopListening(); return; }
    if (!currentQ) return;
    wantsListeningRef.current = true;
    setIsListening(true);
    startOneShot(currentQ.id);
  };

  useEffect(() => {
    return () => { wantsListeningRef.current = false; if (recognitionRef.current) recognitionRef.current.abort(); };
  }, [currentIndex]);

  const goNext = () => { stopListening(); if (currentIndex < questions.length - 1) setCurrentIndex(currentIndex + 1); };
  const goPrev = () => { stopListening(); if (currentIndex > 0) setCurrentIndex(currentIndex - 1); };

  const handleSubmit = async () => {
    if (answeredCount < 6) {
      toast.error(language === 'fr' ? 'Réponds à au moins 6 questions' : 'Answer at least 6 questions');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/quick-quiz/submit`, { answers });
      await fetchUser();
      navigate('/mini-results');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate('/dashboard')} className="text-stone-500" data-testid="quiz-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#FF6B6B]" />
            <span className="font-serif font-semibold text-stone-800 text-sm">
              {language === 'fr' ? 'Quiz Rapide' : 'Quick Quiz'}
            </span>
          </div>
          <span className="text-xs text-stone-400 font-medium">{currentIndex + 1}/{questions.length}</span>
        </div>
        {/* Progress bar */}
        <div className="max-w-2xl mx-auto mt-2">
          <div className="h-1 bg-stone-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: currentQ?.color || '#FF6B6B' }} />
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Module tag */}
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: `${currentQ?.color}15` }}>
            <Icon className="w-3.5 h-3.5" style={{ color: currentQ?.color }} />
          </div>
          <span className="text-xs font-medium uppercase tracking-wider" style={{ color: currentQ?.color }}>{currentQ?.label}</span>
        </div>

        {/* Question */}
        <h2 className="font-serif text-xl sm:text-2xl font-semibold text-stone-900 mb-6 leading-relaxed" data-testid="quiz-question">
          {currentQ?.question}
        </h2>

        {/* Answer area */}
        <div className="relative mb-4">
          <Textarea
            value={answers[currentQ?.id] || ''}
            onChange={(e) => setAnswers(prev => ({ ...prev, [currentQ?.id]: e.target.value }))}
            placeholder={currentQ?.placeholder}
            className={`input-ikigai min-h-[160px] text-base resize-none pr-14 ${isListening ? 'ring-2 ring-[#FF6B6B]/40' : ''}`}
            data-testid="quiz-answer"
          />
          {speechSupported && (
            <button
              type="button"
              onClick={toggleListening}
              className={`absolute bottom-4 right-4 w-11 h-11 rounded-full flex items-center justify-center transition-all shadow-md ${
                isListening 
                  ? 'bg-[#FF6B6B] text-white animate-pulse shadow-[#FF6B6B]/30' 
                  : 'bg-white text-stone-400 hover:text-[#FF6B6B] hover:shadow-lg border border-stone-200'
              }`}
              data-testid="quiz-voice-btn"
            >
              {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>
          )}
        </div>

        {isListening && (
          <p className="text-sm text-[#FF6B6B] font-medium flex items-center gap-1.5 mb-4">
            <span className="w-2 h-2 rounded-full bg-[#FF6B6B] animate-pulse" />
            {language === 'fr' ? 'Parle, j\'écoute...' : 'Speak, I\'m listening...'}
          </p>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <Button
            onClick={goPrev}
            disabled={currentIndex === 0}
            variant="outline"
            className="rounded-xl"
            data-testid="quiz-prev"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {language === 'fr' ? 'Précédent' : 'Previous'}
          </Button>

          {currentIndex < questions.length - 1 ? (
            <Button
              onClick={goNext}
              className="bg-stone-900 hover:bg-stone-800 text-white rounded-xl"
              data-testid="quiz-next"
            >
              {language === 'fr' ? 'Suivant' : 'Next'}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting || answeredCount < 6}
              className="btn-primary flex items-center gap-2"
              data-testid="quiz-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {language === 'fr' ? 'Analyse en cours...' : 'Analyzing...'}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  {language === 'fr' ? 'Découvrir mon aperçu' : 'Discover my preview'}
                </>
              )}
            </Button>
          )}
        </div>

        {/* Answer count */}
        <p className="text-center text-xs text-stone-400 mt-4">
          {language === 'fr' 
            ? `${answeredCount}/8 questions répondues (minimum 6)`
            : `${answeredCount}/8 questions answered (minimum 6)`}
        </p>
      </main>
    </div>
  );
};

export default QuickQuiz;
